// apiUtils.js에서 정의된 함수들을 컴포넌트에서 사용하기 위한 연결 함수
import { editStore, imageStore, settingsStore, sessionStore } from '../stores';
import { get } from 'svelte/store';
import * as apiClient from './apiClient';
import { addToHistory } from './editUtils';
import { API_BASE_URL } from '../utils/config';
import { compressImage } from './imageUtils';
import { persistentOriginalForEdit } from './taskImageUtils';
import { requestLogin, refreshBalance } from './authSession';
import { assertServiceAvailable } from './serviceAvailability';
import { featureCost } from '../stores/costStore';
import { stylesForFeature, fruitOptions, groomingBreeds } from './styleCatalog';
import featureCatalog from '../components/features';

// apiClient에서 제공하는 constructUrl 사용
const constructUrl = apiClient.constructUrl;

// Flutter 앱에서 이름을 받는 스타일과 같은 목록입니다.
export const SUBJECT_NAME_STYLE_NAMES = new Set([
  '잡지 포스터',
  '띠부띠부 씰',
  '포켓몬 카드',
  '현상금 포스터',
  '스노우볼'
]);

const HANGUL_NAME_PATTERN = /[가-힣ㄱ-ㅎㅏ-ㅣ]/;

// 앱과 동일하게 한글 이름은 10자, 그 외 이름은 20자로 제한합니다.
export function limitSubjectName(value = '') {
  const normalized = String(value).replace(/\s+/g, ' ').trimStart();
  const limit = HANGUL_NAME_PATTERN.test(normalized) ? 10 : 20;
  return Array.from(normalized).slice(0, limit).join('');
}

// 대시보드 작업 추적을 위한 함수들
// 이전 작업들이 있을 수 있으므로 현재 스토어의 최대 ID를 기준으로 계산한다
function generateTaskId() {
  const tasks = get(editStore).dashboardTasks || [];
  const maxId = tasks.reduce((m, t) => {
    const idNum = typeof t.id === 'number' ? t.id : parseInt(t.id, 10);
    return idNum > m ? idNum : m;
  }, 0);
  return maxId + 1;
}

// 대시보드에 작업 추가
export function addTaskToDashboard(feature, originalImage) {
  const store = get(editStore);

  // 현재 진행중인 작업이 있다면 새 작업을 만들지 않고 해당 작업을 업데이트
  if (store.currentTaskId) {
    const taskId = store.currentTaskId;

    const updated = store.dashboardTasks.map(t =>
      t.id === taskId
        ? {
            ...t,
            feature: feature.name,
            featureId: feature.id,
            completed: false,
            // 편집 시작 시간을 갱신하여 최근 편집 시점을 표시
            startTime: Date.now()
          }
        : t
    );

    editStore.update(s => ({ ...s, dashboardTasks: updated }));

    return taskId;
  }

  // 작업 ID 생성 (신규 작업)
  const newTaskId = generateTaskId();

  // 새 작업 생성 (이미지 전용 편집 히스토리 포함)
  const task = {
    id: newTaskId,
    feature: feature.name,
    featureId: feature.id,
    startTime: Date.now(),
    completed: false,
    originalImage: originalImage,
    editHistory: []  // 이 작업 전용 빈 편집 히스토리 초기화
  };

  // 태스크 ID를 글로벌 변수로도 저장 (디버깅용)
  window.lastTaskId = newTaskId;
  console.log(`신규 작업 ID: ${newTaskId}, 이미지: ${originalImage.slice(0, 50)}...`);

  // 대시보드에 작업 추가
  editStore.update(store => ({
    ...store,
    dashboardTasks: [task, ...store.dashboardTasks],
    currentTaskId: newTaskId
  }));

  const session = get(sessionStore);
  if (session.loggedIn && !session.isGuest) {
    apiClient.saveTasksToServer(session.userId, session.sessionId, get(editStore).dashboardTasks);
  }
  
  // 편집기에서 대시보드로 이동했을 때 로딩 오버레이를 숨기기 위한 플래그 설정
  if (window.location.pathname.includes('dashboard')) {
    editStore.update(store => ({
      ...store,
      isLoadingInDashboard: true,
      isLoading: false // 편집기에서 로딩 오버레이를 숨김
    }));
  }
  
  return newTaskId;
}

// 대시보드에서 작업 삭제
export function removeTaskFromDashboard(taskId) {
  editStore.update(store => ({
    ...store,
    dashboardTasks: store.dashboardTasks.filter(t => t.id !== taskId)
  }));

  const session = get(sessionStore);
  if (session.loggedIn && !session.isGuest) {
    apiClient.saveTasksToServer(session.userId, session.sessionId, get(editStore).dashboardTasks);
  }
}

// 대시보드 작업 완료
export function completeTask(taskId, resultImage, processId = null, resultImages = null, historyOverride = null) {
  // 현재 편집 히스토리와 현재 작업 이미지 가져오기
  const editStore_ = get(editStore);
  
  console.log(`작업 완료: ID=${taskId}, 현재 히스토리 항목 수=${editStore_.editHistory.length}, processId=${processId}`);
  
  // 현재 작업 전용 편집 히스토리 복사본 생성 (깊은 복사 사용)
  const currentTaskHistory = JSON.parse(
    JSON.stringify(
      historyOverride || editStore_.editHistory.filter(item => item.taskId === taskId)
    )
  );
  
  // 2장 이상일 때 needsSelection 플래그 설정
  const needsSelection = resultImages && resultImages.length >= 2;

  editStore.update(store => {
    // 업데이트할 작업 찾기
    const updatedTasks = store.dashboardTasks.map(task => 
      task.id === taskId 
        ? { 
            ...task, 
            completed: true, 
            status: 'completed',
            endTime: Date.now(), 
            resultImage,
            resultImages: needsSelection ? resultImages : null,
            needsSelection: needsSelection || false,
            process_id: processId,
            // 현재 작업의 편집 히스토리만 저장 (다른 작업과 공유하지 않음)
            editHistory: currentTaskHistory 
          }
        : task
    );
    
    return {
      ...store,
      dashboardTasks: updatedTasks,
      isLoading: false,  // 로딩 상태 해제
      isLoadingInDashboard: false  // 대시보드 로딩 상태도 해제
    };
  });
  
  // 작업 완료 이벤트 발생
  const task = get(editStore).dashboardTasks.find(t => t.id === taskId);
  if (task) {
    document.dispatchEvent(new CustomEvent('taskCompleted', {
      detail: { ...task, resultImage, process_id: processId, editHistory: currentTaskHistory }
    }));

    const session = get(sessionStore);
    if (session.loggedIn && !session.isGuest) {
      apiClient.saveTasksToServer(session.userId, session.sessionId, get(editStore).dashboardTasks);
    }
  }
}

// 선택된 기능 적용하기
let generationInFlight = false;
export async function applySelectedFeature() {
  if (generationInFlight) return;
  generationInFlight = true;
  try { return await runSelectedFeature(); }
  finally { generationInFlight = false; }
}
async function runSelectedFeature() {
  const feature = get(editStore).selectedFeature;
  const outputFormat = get(settingsStore).outputFormat;
  
  // 현재 작업이 이미 대시보드로 이동했다면 hideOverlayForCurrentTask를 초기화
  editStore.update(store => ({
    ...store,
    hideOverlayForCurrentTask: false
  }));
  
  // 체인 형식으로 이전 결과 이미지 사용
  // 결과 이미지가 있으면 해당 이미지 사용, 없으면 업로드된 이미지 사용
  let file;
  try {
    const imgStore = get(imageStore);
    if (imgStore.resultImage) {
      file = await urlToFile(imgStore.resultImage, 'result.png');
    } else if (imgStore.uploadedImage) {
      file = imgStore.uploadedImage;
    } else if (imgStore.uploadedImageUrl) {
      // 편집기가 대시보드에서 로드된 경우 uploadedImage가 없을 수 있으므로 URL을 파일로 변환
      file = await urlToFile(imgStore.uploadedImageUrl, 'original.png');
    }
    if (file && file.size > 900 * 1024) {
      try {
        file = await compressImage(file);
      } catch (e) {
        console.error('이미지 압축 실패:', e);
      }
    }
  } catch (error) {
    console.error('이미지 파일 변환 중 오류:', error);
    alert('이미지 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
    return;
  }
  
  if (!feature) {
    alert('편집 기능을 선택해주세요.');
    return;
  }

  if (feature.disabled || featureCatalog.find(item => item.id === feature.id)?.disabled) {
    alert(`${feature.name} 기능은 현재 준비 중입니다.`);
    return;
  }

  if (!file) {
    alert('이미지를 먼저 업로드해주세요.');
    return;
  }

  const settings = get(settingsStore);
  if (['style_transform', 'advanced_style_transform', 'emoticon_style'].includes(feature.id)
    && !stylesForFeature(feature.id).some(style => style.name === settings.selectedStyle)) {
    alert('스타일을 선택해 주세요.'); return;
  }
  if (settings.selectedStyle === '과일 인형탈' && !fruitOptions.includes(settings.fruitType)) {
    alert('과일을 선택해 주세요.'); return;
  }
  if (settings.selectedStyle === '미용 스타일' && !groomingBreeds.includes(settings.groomingBreed)) {
    alert('반려견 품종을 선택해 주세요.'); return;
  }
  if (feature.id === 'find_mbti' && (!settings.mbtiQuizCompleted || !/^[EI][SN][TF][JP]$/.test(settings.mbtiType))) {
    alert('MBTI 테스트를 먼저 완료해 주세요.'); return;
  }
  if (feature.id === 'image_composite' && !(settings.secondaryImageFile instanceof Blob)) {
    alert('합성할 이미지를 선택해 주세요.'); return;
  }
  if (!await requestLogin('준비한 사진과 옵션으로 생성하려면 로그인해 주세요.')) return;
  try {
    await assertServiceAvailable();
    const balance = await refreshBalance();
    if ((balance?.total_rion ?? balance?.balance ?? 0) < featureCost(feature.id)) {
      alert('RION이 부족합니다. 내 정보에서 출석 보상과 쿠폰을 확인하거나 앱에서 충전해 주세요.'); return;
    }
  } catch (error) { alert(error.message); return; }

  const selectedStyle = get(settingsStore).selectedStyle;
  const needsSubjectName = SUBJECT_NAME_STYLE_NAMES.has(selectedStyle);
  const subjectName = needsSubjectName
    ? limitSubjectName(get(settingsStore).subjectName).trim() || 'puppy'
    : '';

  if (needsSubjectName && subjectName !== get(settingsStore).subjectName) {
    settingsStore.update(store => ({ ...store, subjectName }));
  }

  if (feature.id === 'outpaint') {
    const { left, right, up, down } = get(settingsStore).outpaintSettings;
    if (left >= 2000 || right >= 2000 || up >= 2000 || down >= 2000) {
      alert('확장하려는 영역이 너무 큽니다. 각 방향은 2000px 미만이어야 합니다.');
      return;
    }
  }
  
  // 현재 작업 중인 이미지 URL 저장
  let originalImageUrl = persistentOriginalForEdit(get(imageStore));
  if (!originalImageUrl) {
    try {
      originalImageUrl = await apiClient.uploadImage(file);
      const currentImageState = get(imageStore);
      imageStore.update(imageState => ({
        ...imageState,
        ...(currentImageState.resultImage
          ? { resultImage: originalImageUrl }
          : { serverImageUrl: originalImageUrl }),
      }));
    } catch (error) {
      console.error('원본 이미지 영구 URL 확보 실패:', error);
      alert('원본 이미지를 저장하지 못했습니다. 잠시 후 다시 시도해주세요.');
      return;
    }
  }
  
  // 새 작업인지 확인 (새 이미지 업로드 또는 편집기 탭 처음 진입)
  const store = get(editStore);
  const imgStore = get(imageStore);
  const currentBaseOriginalUrl =
    imgStore.serverImageUrl || imgStore.uploadedImageUrl;
  const isNewTask = !store.currentTaskId ||
                   (imgStore.taskOriginalImageUrl &&
                    imgStore.taskOriginalImageUrl !== currentBaseOriginalUrl);
  
  // 새 작업인 경우 편집 히스토리 초기화
  if (isNewTask) {
    console.log("새 작업 감지: 편집 히스토리 초기화");
    editStore.update(store => ({
      ...store,
      editHistory: [],  // 편집 히스토리 초기화
      currentEditIndex: -1,
      currentTaskId: null
    }));
    
    // 현재 작업 이미지 URL 업데이트
    imageStore.update(store => ({
      ...store,
      currentTaskImageUrl: originalImageUrl,
      taskOriginalImageUrl: imgStore.serverImageUrl || originalImageUrl
    }));
    
    // 디버깅 로그
    console.log("작업 초기화:", {
      isNewTask,
      originalImageUrl,
      currentTaskImageUrl: imgStore.currentTaskImageUrl
    });
  }
  
  // 대시보드에 작업 추가
  const taskId = addTaskToDashboard(feature, originalImageUrl);
  const appliedSettings = JSON.parse(JSON.stringify(get(settingsStore)));
  const generationOwner = get(sessionStore).userId;
  const priorHistory = get(editStore).editHistory.filter(item => item.taskId === taskId);
  
  // 로딩 상태 설정
  editStore.update(store => ({
    ...store,
    isLoading: true,
    loadingText: `${feature.name} 기능을 적용하는 중입니다...`
  }));
  
  try {
    let result;
    
    switch (feature.id) {
      case 'remove_background':
        result = await apiClient.removeBackground(file, outputFormat, {});
        break;
        
      case 'replace_background':
        result = await apiClient.replaceBackground(
          file, 
          get(settingsStore).selectedBackground, 
          outputFormat,
          { chooseBackground: get(settingsStore).selectedBackground }
        );
        break;
        
      case 'search_replace':
        result = await apiClient.searchReplace(
          file, 
          get(settingsStore).searchReplacePrompt, 
          outputFormat,
          { prompt: get(settingsStore).searchReplacePrompt }
        );
        break;
        
      case 'search_recolor':
        result = await apiClient.searchRecolor(
          file, 
          get(settingsStore).searchRecolorColor, 
          outputFormat,
          { color: get(settingsStore).searchRecolorColor }
        );
        break;
        
      case 'outpaint':
        const outpaintSettings = get(settingsStore).outpaintSettings;

        // 이미지 위치 조정 값을 확장 픽셀에 반영
        const finalLeft = Math.round(outpaintSettings.left + outpaintSettings.imagePosition.x);
        const finalRight = Math.round(outpaintSettings.right - outpaintSettings.imagePosition.x);
        const finalUp = Math.round(outpaintSettings.up + outpaintSettings.imagePosition.y);
        const finalDown = Math.round(outpaintSettings.down - outpaintSettings.imagePosition.y);

        result = await apiClient.outpaintImage(
          file,
          finalLeft,
          finalRight,
          finalUp,
          finalDown,
          outpaintSettings.creativity,
          outpaintSettings.prompt,
          outputFormat,
          { left: finalLeft, right: finalRight, up: finalUp, down: finalDown, prompt: outpaintSettings.prompt }
        );
        break;
        
      case 'style_transform':
        result = await apiClient.transformStyle(
          file, 
          get(settingsStore).selectedStyle,
          get(settingsStore).controlStrength,
          get(settingsStore).preserveFace,
          outputFormat,
          {
            style: get(settingsStore).selectedStyle,
            style_backend: stylesForFeature(feature.id).find(style => style.name === get(settingsStore).selectedStyle)?.backend,
            subject_name: subjectName || undefined,
            fruit_type: get(settingsStore).selectedStyle === '과일 인형탈' ? get(settingsStore).fruitType : undefined,
            controlStrength: get(settingsStore).controlStrength,
            preserveFace: get(settingsStore).preserveFace
          }
        );
        break;

      case 'advanced_style_transform':
      case 'emoticon_style': {
        const selectedStyle = get(settingsStore).selectedStyle;
        const backendStyle = stylesForFeature(feature.id).find(style => style.name === selectedStyle)?.backendName || selectedStyle;
        result = await apiClient.transformAdvancedStyle(
          file,
          backendStyle,
          outputFormat,
          { style: backendStyle, subject_name: subjectName || undefined,
            grooming_breed: selectedStyle === '미용 스타일' ? get(settingsStore).groomingBreed : undefined }
        );
        break;
      }

      case 'find_mbti':
        result = await apiClient.createMbtiImage(
          file,
          get(settingsStore).mbtiType,
          outputFormat,
          { mbti_type: get(settingsStore).mbtiType }
        );
        break;

      case 'make_video':
        result = await apiClient.createVideo(
          file,
          get(settingsStore).videoStyle,
          { video_style: get(settingsStore).videoStyle }
        );
        break;
        
      case 'inpaint':
        // 캔버스 데이터 가져오기
        const maskImage = get(imageStore).canvasData;
        if (!maskImage) {
          alert('마스크를 그려주세요.');
          editStore.update(store => ({ ...store, isLoading: false }));
          removeTaskFromDashboard(taskId);
          return;
        }
        
        const inpaintSettings = get(settingsStore).inpaintSettings;
        result = await apiClient.inpaintImage(
          file, 
          maskImage, 
          inpaintSettings.prompt, 
          inpaintSettings.negativePrompt, 
          inpaintSettings.seed, 
          outputFormat,
          {
            prompt: inpaintSettings.prompt,
            negative_prompt: inpaintSettings.negativePrompt,
            seed: inpaintSettings.seed
          }
        );
        break;
        
      case 'resolution_increase':
        result = await apiClient.increaseResolution(file, outputFormat, {});
        break;
        
      case 'object_remove':
        result = await apiClient.removeObject(
          file, 
          get(settingsStore).removeObjectsPrompt,
          { remove_objects: get(settingsStore).removeObjectsPrompt }
        );
        break;

      case 'image_composite':
        const secondaryFile = get(settingsStore).secondaryImageFile;
        if (!secondaryFile) {
          alert('합성할 이미지를 선택해주세요.');
          editStore.update(store => ({ ...store, isLoading: false }));
          removeTaskFromDashboard(taskId);
          return;
        }
        result = await apiClient.compositeImage(file, secondaryFile, {
          composite_mode: get(settingsStore).compositeMode
        });
        break;

      case 'erase_object':
        // 캔버스 데이터 가져오기
        const eraseMask = get(imageStore).canvasData;
        if (!eraseMask) {
          alert('지울 영역을 그려주세요.');
          editStore.update(store => ({ ...store, isLoading: false }));
          removeTaskFromDashboard(taskId);
          return;
        }
        
        result = await apiClient.eraseObject(
          file, 
          eraseMask, 
          outputFormat, 
          get(settingsStore).eraseObjectSeed,
          { seed: get(settingsStore).eraseObjectSeed }
        );
        break;
        
      default:
        console.error('지원하지 않는 기능입니다:', feature.id);
        alert('지원하지 않는 기능입니다.');
        editStore.update(store => ({ ...store, isLoading: false }));
        removeTaskFromDashboard(taskId);
        return;
    }
    
    if (get(sessionStore).userId !== generationOwner) return;
    // 결과가 있으면 이미지 스토어 업데이트
    if (result && (result.result_image_path || result.result_image_b64 || result.download_url)) {
      const resultImageUrl = result.result_image_b64
        ? `data:image/${outputFormat};base64,${result.result_image_b64}`
        : constructUrl(result.download_url || result.result_image_path);
      const resultImages = result.result_urls ? result.result_urls.map(u => constructUrl(u)) : null;
      
      // 이미지 스토어 업데이트
      const stillActive = get(editStore).currentTaskId === taskId;
      if (stillActive) imageStore.update(store => ({
        ...store,
        resultImage: resultImageUrl,
        resultImages: resultImages,
        currentTaskImageUrl: resultImageUrl
      }));
      
      console.log(`${feature.name} 기능 적용 완료, 결과 이미지 업데이트됨`);
      
      // 편집 기록에 추가 (결과 이미지 업데이트 후)
      const currentResultImage = resultImageUrl;
      if (stillActive) addToHistory(feature, appliedSettings, currentResultImage, taskId, result?.process_id);
      
      // 대시보드 작업 완료 처리 (편집 기록 추가 후에 실행)
      const detachedHistory = stillActive ? null : [...priorHistory, { feature, settings: appliedSettings,
        resultImage: currentResultImage, taskId, processId: result?.process_id, timestamp: Date.now() }];
      completeTask(taskId, resultImageUrl, result?.process_id, resultImages, detachedHistory);
      if (feature.id === 'style_transform') {
        settingsStore.update(store => ({ ...store, recentStyles: [appliedSettings.selectedStyle, ...(store.recentStyles || []).filter(name => name !== appliedSettings.selectedStyle)].slice(0, 3) }));
      }
      
      // 결과 탭으로 자동 이동
      // editStore.update(store => ({
      //   ...store,
      //   selectedTab: 'result'
      // }));
    } else {
      console.error('결과 이미지가 없습니다.');
      alert('이미지 처리 결과를 받지 못했습니다. 다시 시도해주세요.');
      removeTaskFromDashboard(taskId);
    }
    
    // 자동으로 결과 탭으로 이동하지 않음 (사용자가 편집 마무리 버튼을 누를 때까지 편집 탭 유지)
    
  } catch (error) {
    console.error('기능 적용 중 오류:', error);
    editStore.update(store => ({ ...store, dashboardTasks: store.dashboardTasks.map(task => task.id === taskId
      ? { ...task, status: error.pendingTask ? 'queued' : 'failed', error: error.message } : task) }));
    alert(`오류가 발생했습니다: ${error.message}`);
  } finally {
    refreshBalance().catch(() => {});
    // 로딩 상태 해제
    editStore.update(store => ({
      ...store,
      isLoading: false
    }));
  }
}

// Base64 데이터 URL을 File 객체로 변환하는 유틸리티 함수
function dataURLtoFile(dataurl, filename) {
  if (!dataurl) return null;
  
  try {
    // 데이터 URL 형식 검증
    if (!dataurl.startsWith('data:') || dataurl.indexOf(',') === -1) {
      console.error('유효하지 않은 데이터 URL 형식입니다:', dataurl.substring(0, 30) + '...');
      return null;
    }
    
    const arr = dataurl.split(',');
    if (!arr[0] || !arr[1]) {
      console.error('데이터 URL 분할 실패:', dataurl.substring(0, 30) + '...');
      return null;
    }
    
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch || !mimeMatch[1]) {
      console.error('MIME 타입을 찾을 수 없습니다:', arr[0]);
      // 기본 MIME 타입 사용
      const mime = 'image/png';
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      
      return new File([u8arr], filename, { type: mime });
    }
    
    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new File([u8arr], filename, { type: mime });
  } catch (error) {
    console.error('데이터 URL을 파일로 변환 중 오류 발생:', error);
    return null;
  }
}

// 새 함수: URL을 File 객체로 변환 (data URL, blob URL 모두 처리)
async function urlToFile(url, filename) {
  if (url.startsWith('data:')) {
    // data URL인 경우 기존 함수를 사용
    return dataURLtoFile(url, filename);
  } else if (url.startsWith('blob:')) {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new File([blob], filename, { type: blob.type });
    } catch (err) {
      console.error('blob URL을 파일로 변환 중 오류 발생:', err);
      return null;
    }
  } else if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const response = await fetch(url, { mode: 'cors' });
      const blob = await response.blob();
      const mime = blob.type || 'image/png';
      return new File([blob], filename, { type: mime });
    } catch (err) {
      console.error('원격 URL을 파일로 변환 중 오류 발생:', err);
      return null;
    }
  } else {
    // 상대 경로일 수 있으므로 구성을 통해 전체 URL 생성
    const targetUrl = constructUrl(url);
    try {
      const response = await fetch(targetUrl, { mode: 'cors' });
      const blob = await response.blob();
      const mime = blob.type || 'image/png';
      return new File([blob], filename, { type: mime });
    } catch (err) {
      console.error('지원하지 않는 URL 형식입니다:', url);
      return null;
    }
  }
}

// Hugging Face Autotrain 파인튜닝 시작
export async function startAutotrainFinetuning(projectName, description, images) {
  try {
    const response = await fetch('/api/autotrain/start', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        project_name: projectName,
        description: description,
        images: images
      })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || '파인튜닝 시작 중 오류가 발생했습니다.');
    }
    
    return data;
  } catch (error) {
    console.error('파인튜닝 시작 오류:', error);
    throw error;
  }
}

// Hugging Face Autotrain 상태 확인
export async function checkAutotrainStatus(modelId) {
  try {
    const response = await fetch(`/api/autotrain/status?model_id=${modelId}`);
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || '파인튜닝 상태 확인 중 오류가 발생했습니다.');
    }
    
    return data;
  } catch (error) {
    console.error('파인튜닝 상태 확인 오류:', error);
    throw error;
  }
}
